import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMap, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";

import { useIsComponentMounted } from "../../../hooks/useIsComponentMounted";
import { useNitroStore } from "../../../store/Store";
import NitroMarker from "./NitroMarker";

import ICompAnalysis from "../../../types/ICompAnalysis";

import "../../../App.css";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useIsMobile } from "../../../hooks/useIsMobile";

const RoutesLayer = () => {
  const isComponentMounted = useIsComponentMounted();
  const map = useMap();

  const [selectedLocation, labelType] = useNitroStore((state) => [
    state.selectedLocation,
    state.labelType,
  ]);

  const isTablet = useIsMobile(1334);

  const [showCompetitiveAnalysisModal, mode] = useNitroStore((state) => [
    state.showCompetitiveAnalysisModal,
    state.mode,
  ]);
  const [kochRecords, competitorRecords, hiddenOrigins] = useNitroCAStore(
    (state) => [state.kochRecords, state.competitorRecords, state.hiddenOrigins]
  );

  const hiddenTruckOrigins = useMemo(
    () => Array.from(hiddenOrigins.truck),
    [hiddenOrigins.truck]
  );
  const hiddenRailOrigins = useMemo(
    () => Array.from(hiddenOrigins.rail),
    [hiddenOrigins.rail]
  );
  const hiddenTruckKey = useMemo(
    () => hiddenTruckOrigins.join(","),
    [hiddenTruckOrigins]
  );
  const hiddenRailKey = useMemo(
    () => hiddenRailOrigins.join(","),
    [hiddenRailOrigins]
  );

  const routesRef = useRef<any[] | null>(null);
  const [markers, setMarkers] = useState<any[]>([]);

  const [tooltipLatLng, setTooltipLatLng] = useState<
    { lat: number; lng: number } | undefined
  >(undefined);
  const [selectedRoute, setSelectedRoute] = useState<ICompAnalysis | undefined>(
    undefined
  );

  const getLabel = useCallback(
    (route: ICompAnalysis, type: "origin" | "destination") => {
      if (type === "destination") return "";
      if (!route[type]) return "";
      let label = route[type].city;
      if (labelType === "prices") {
        const values = [];
        if (route[type].name) {
          values.push(route[type].name);
        }
        if (route.maxPrice) {
          values.push(`$${route.maxPrice}`);
        }

        label = values.join(" - ");
      } else if (labelType === "city") {
        label = `${(route[type] as any).city}`;
      } else if (labelType === "location") {
        label = route[type].name;
      } else if (labelType === "facility") {
        label = route[type].owner;
      }

      return label;
    },
    [labelType]
  );

  const load = useCallback(async () => {
    const currentModeKey = mode === "TRUCK" ? "truck" : "rail";
    const filteredKochRecords = kochRecords.filter((record) => {
      return !hiddenOrigins[currentModeKey].has(record.origin.name);
    });
    const filteredCompetitorRecords = competitorRecords.filter((record) => {
      return !hiddenOrigins[currentModeKey].has(record.origin.name);
    });

    const selectedRoutes = [
      ...filteredKochRecords,
      ...filteredCompetitorRecords,
    ];

    console.info("Loading routes", {
      selectedRoutes,
      hiddenOrigins: Array.from(hiddenOrigins[currentModeKey]),
      mode: currentModeKey,
    });
    routesRef.current = [];
    const newMarkers: JSX.Element[] = [];
    // markersRef.current = [];

    const latLngBounds = new google.maps.LatLngBounds();

    const groupedRoutes = selectedRoutes.reduce((acc, route) => {
      const key = `${route.origin.id}-${route.destination?.id}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(route);
      return acc;
    }, {} as { [key: string]: ICompAnalysis[] });

    Object.values(groupedRoutes).forEach((routes, i) => {
      // selectedRoutes.forEach((route: ICompAnalysis, i) => {

      const route = routes[0];
      const occupantTypes: string[] = [];
      routes.forEach((r: any) =>
        r.occupantTypes.forEach((x: any) => occupantTypes.push(x))
      );

      let first;
      let last;
      if (route.origin && route.destination) {
        last = {
          lat: route.origin.coordinate[0],
          lng: route.origin.coordinate[1],
        };
        first = {
          lat: route.destination.coordinate[0],
          lng: route.destination.coordinate[1],
        };
      } else {
        last = {
          lat: route.origin.coordinate[0],
          lng: route.origin.coordinate[1],
        };
        first = {
          lat: route.coordinates?.[route.coordinates.length - 1]?.[1] ?? 0,
          lng: route.coordinates?.[route.coordinates.length - 1]?.[0] ?? 0,
        };
      }

      if (i === 0) {
        latLngBounds.extend(new google.maps.LatLng(first));
        const label = getLabel(route, "destination");

        // If they click on a zipCode (gray pin), needs to stay a gray pin.
        // const type = currentFilters.city ? "zipCodes" : "customer";
        newMarkers.push(
          <AdvancedMarker position={first} key="first">
            <NitroMarker
              type={selectedLocation?.pinType || "zipCodes"}
              label={label}
            />
          </AdvancedMarker>
        );
      }

      const line = new google.maps.Polyline({
        path: [first, last],
        strokeWeight: 1,
        strokeColor: "black",
      });

      google.maps.event.addListener(line, "mouseover", ({ latLng }: any) => {
        line.setOptions({ strokeWeight: 4 });
        setTooltipLatLng({ lat: latLng.lat(), lng: latLng.lng() });
        setSelectedRoute(route);
      });
      google.maps.event.addListener(line, "mouseout", () => {
        line.setOptions({ strokeWeight: 2 });
        setTooltipLatLng(undefined);
        setSelectedRoute(undefined);
      });

      line.setMap(map);
      routesRef.current?.push(line);

      const label = getLabel(route, "origin");

      latLngBounds.extend(new google.maps.LatLng(last));

      let type = "koch";
      if (
        occupantTypes.indexOf("Koch") !== -1 &&
        occupantTypes.indexOf("Competitor") !== -1
      ) {
        type = "kochAndCompetitor";
      } else if (occupantTypes.indexOf("Koch") !== -1) {
        type = "koch";
      } else if (occupantTypes.indexOf("Competitor") !== -1) {
        type = "competitor";
      }

      newMarkers.push(
        <AdvancedMarker
          position={last}
          // eslint-disable-next-line react/no-array-index-key
          key={`last${i}`}
          // style={{ opacity: isSelectedRoute ? 1 : 0.7 }}
        >
          <NitroMarker type={type} label={label} />
        </AdvancedMarker>
      );
    });
    console.debug("Routes loaded -------", {
      markers: newMarkers.length,
      routes: routesRef.current?.length,
    });
    setMarkers(newMarkers);

    // latitude 90 - -90
    // longitude 180 - -180

    if (selectedRoutes.length > 0) {
      map?.fitBounds(latLngBounds, {
        left: isTablet ? 60 : 540,
        right: isTablet ? 40 : 80,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    kochRecords,
    competitorRecords,
    hiddenTruckKey,
    hiddenRailKey,
    mode,
    // selectedKochRecord,
    getLabel,
  ]);

  useEffect(() => {
    if (!isComponentMounted) return;
    if (!map) return;

    console.log("Routes clearing", {
      markers: markers.length,
      routes: routesRef.current?.length,
      kochRecords: kochRecords.length,
      competitorRecords: competitorRecords.length,
    });

    routesRef.current?.forEach((r) => {
      r.setMap(null);
    });

    if (
      ((!kochRecords || kochRecords.length === 0) &&
        (!competitorRecords || competitorRecords.length === 0)) ||
      !showCompetitiveAnalysisModal
    ) {
      routesRef.current = null;
      // markersRef.current = null;
      setMarkers([]);
      console.log("Clear markers - nothing to show", {
        kochRecords,
        competitorRecords,
        showCompetitiveAnalysisModal,
      });
      return;
    }

    console.info("Routes Layer Loading....");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    kochRecords,
    competitorRecords,
    labelType,
    showCompetitiveAnalysisModal,
    mode,
    hiddenTruckKey,
    hiddenRailKey,
  ]);

  return (
    <>
      {markers}
      {selectedRoute && tooltipLatLng && (
        <InfoWindow position={tooltipLatLng}>
          <div
            style={{
              backgroundColor: "white",
              color: "#383838",
              fontSize: 16,
            }}
          >
            {selectedRoute.mode === "RAIL" && (
              <>
                <div
                  style={{
                    padding: "0px 20px 5px 20px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {/* <NitroMarkerBase width={28} type={selectedRoute.} /> */}
                  <b>{`${selectedRoute.origin.city}`}</b>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    paddingLeft: 25,
                    paddingBottom: 20,
                    fontSize: 14,
                  }}
                >
                  <div>{`Freight: $${selectedRoute.freight.toFixed(2)}`}</div>
                  <div>
                    {`Mode: ${selectedRoute.mode} (${selectedRoute.legs[0].minCars})`}
                  </div>
                </div>
              </>
            )}

            {selectedRoute.mode !== "RAIL" && (
              <>
                <div style={{ padding: "0px 20px 5px 20px" }}>
                  <b>{`${selectedRoute.origin.city} to ${selectedRoute.destination?.city}`}</b>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    paddingLeft: 25,
                    paddingBottom: 20,
                    fontSize: 14,
                  }}
                >
                  <div>{`Freight: $${selectedRoute.freight}`}</div>
                  <div> {`Mode: ${selectedRoute.mode}`}</div>
                </div>
              </>
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
};

export default RoutesLayer;
